import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';

export default function Instances() {
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('BOARD');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: authData } = useQuery({
    queryKey: ['auth'],
    queryFn: api.auth.me,
  });

  const { data: instancesData } = useQuery({
    queryKey: ['instances'],
    queryFn: api.instances.list,
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.instances.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['instances']);
      setShowCreate(false);
      setName('');
      setDescription('');
      setType('BOARD');
    },
  });

  const handleCreate = (e) => {
    e.preventDefault();
    createMutation.mutate({ name, description, type });
  };

  const user = authData?.user;
  const instances = instancesData?.instances || [];
  const canCreate = user?.global_role === 'ADMIN' || user?.global_role === 'ORGANIZER';

  const getTypeColor = (type) => {
    switch (type) {
      case 'BOARD':
        return 'bg-sonarges-red-100 text-sonarges-red-700 dark:bg-sonarges-red-900/30 dark:text-sonarges-red-400 border-sonarges-red-300 dark:border-sonarges-red-700';
      case 'COMMITTEE':
        return 'bg-sonarges-green-100 text-sonarges-green-700 dark:bg-sonarges-green-900/30 dark:text-sonarges-green-400 border-sonarges-green-300 dark:border-sonarges-green-700';
      case 'PROJECT':
        return 'bg-sonarges-gray-100 text-sonarges-gray-700 dark:bg-sonarges-gray-700 dark:text-sonarges-gray-300 border-sonarges-gray-300 dark:border-sonarges-gray-600';
      default:
        return 'bg-sonarges-gray-100 text-sonarges-gray-700';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-sonarges-gray-900 dark:text-white">
              {t('instances.title')}
            </h1>
            <p className="mt-2 text-sonarges-gray-600 dark:text-sonarges-gray-400">
              Gérez vos conseils, comités et projets
            </p>
          </div>
          {canCreate && (
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="px-6 py-3 bg-gradient-to-r from-sonarges-green-600 to-sonarges-green-500 text-white font-semibold rounded-lg hover:from-sonarges-green-700 hover:to-sonarges-green-600 transform transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              {showCreate ? (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>{t('common.cancel')}</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{t('instances.create')}</span>
                </>
              )}
            </button>
          )}
        </div>

        {showCreate && (
          <div className="bg-white dark:bg-sonarges-gray-800 rounded-xl shadow-lg p-8 border-2 border-sonarges-green-500 dark:border-sonarges-green-600">
            <h3 className="text-xl font-bold text-sonarges-gray-900 dark:text-white mb-6">
              Nouvelle instance
            </h3>
            <form onSubmit={handleCreate} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300 mb-2">
                  {t('instances.name')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-sonarges-gray-200 dark:border-sonarges-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 focus:border-transparent bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white transition-colors duration-200"
                  required
                  placeholder="Ex: Conseil d'Administration 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300 mb-2">
                  {t('instances.description')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-sonarges-gray-200 dark:border-sonarges-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-sonarges-green-500 focus:border-transparent bg-white dark:bg-sonarges-gray-700 text-sonarges-gray-900 dark:text-white transition-colors duration-200"
                  rows="4"
                  placeholder="Description de l'instance..."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-sonarges-gray-700 dark:text-sonarges-gray-300 mb-2">
                  {t('instances.type')}
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {['BOARD', 'COMMITTEE', 'PROJECT'].map((typeOption) => (
                    <button
                      key={typeOption}
                      type="button"
                      onClick={() => setType(typeOption)}
                      className={`px-4 py-3 rounded-lg font-medium border-2 transition-all duration-200 ${
                        type === typeOption
                          ? getTypeColor(typeOption)
                          : 'border-sonarges-gray-200 dark:border-sonarges-gray-600 text-sonarges-gray-600 dark:text-sonarges-gray-400 hover:border-sonarges-gray-300 dark:hover:border-sonarges-gray-500'
                      }`}
                    >
                      {t(`instances.${typeOption}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-6 py-3 border-2 border-sonarges-gray-300 dark:border-sonarges-gray-600 text-sonarges-gray-700 dark:text-sonarges-gray-300 rounded-lg font-medium hover:bg-sonarges-gray-100 dark:hover:bg-sonarges-gray-700 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-6 py-3 bg-gradient-to-r from-sonarges-green-600 to-sonarges-green-500 text-white font-semibold rounded-lg hover:from-sonarges-green-700 hover:to-sonarges-green-600 disabled:opacity-50 transition-all duration-200 shadow-lg"
                >
                  {createMutation.isPending ? t('auth.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {instances.map((instance) => (
            <div
              key={instance.id}
              onClick={() => navigate(`/instances/${instance.id}`)}
              className="group bg-white dark:bg-sonarges-gray-800 rounded-xl shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-sonarges-green-500 dark:hover:border-sonarges-green-600 transform hover:-translate-y-1"
            >
              <div className={`h-2 ${type === 'BOARD' ? 'bg-sonarges-red-500' : type === 'COMMITTEE' ? 'bg-sonarges-green-500' : 'bg-sonarges-gray-500'}`}></div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-sonarges-gray-900 dark:text-white group-hover:text-sonarges-green-600 dark:group-hover:text-sonarges-green-400 transition-colors">
                    {instance.name}
                  </h3>
                  <span className={`px-3 py-1 text-xs font-bold rounded-full border ${getTypeColor(instance.type)}`}>
                    {t(`instances.${instance.type}`)}
                  </span>
                </div>
                <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400 text-sm mb-4 line-clamp-2">
                  {instance.description || t('instances.noDescription')}
                </p>
                <div className="flex items-center justify-between text-xs text-sonarges-gray-500 dark:text-sonarges-gray-400">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    {t('dashboard.members')}
                  </span>
                  <span className="text-sonarges-green-600 dark:text-sonarges-green-400 font-semibold group-hover:underline">
                    {t('dashboard.viewDetails')} →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {instances.length === 0 && !showCreate && (
          <div className="bg-white dark:bg-sonarges-gray-800 rounded-xl shadow-lg p-12 text-center border-2 border-dashed border-sonarges-gray-300 dark:border-sonarges-gray-600">
            <svg className="w-16 h-16 mx-auto text-sonarges-gray-400 dark:text-sonarges-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-semibold text-sonarges-gray-900 dark:text-white mb-2">
              {t('dashboard.noInstances')}
            </h3>
            <p className="text-sonarges-gray-600 dark:text-sonarges-gray-400 mb-6">
              {t('dashboard.noInstancesDesc')}
            </p>
            {canCreate && (
              <button
                onClick={() => setShowCreate(true)}
                className="inline-flex items-center px-6 py-3 bg-sonarges-green-600 text-white font-semibold rounded-lg hover:bg-sonarges-green-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('instances.create')}
              </button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
